import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Container,
    CircularProgress,
    MenuItem,
    Select,
    InputLabel,
    FormControl
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Configuración de axios
const api = axios.create({
    baseURL: 'https://dymesenergy.gst-panel.com'
});

// Servicios
const DepartamentoService = {
    obtenerTodos: () => api.get('/get/obtener_departamentos.php')
        .then(response => ({
            data: Array.isArray(response.data) ? response.data : response.data?.data || []
        }))
};

const EmpresaService = {
    obtenerPorDepartamento: (idDepartamento) =>
        api.get(`/get/obtener_empresas.php?id_departamento=${idDepartamento}`)
            .then(response => ({
                data: Array.isArray(response.data) ? response.data : response.data?.data || []
            }))
};

const EstratoService = {
    obtenerTodos: () => api.get('/get/obtener_estratos.php')
        .then(response => ({
            data: Array.isArray(response.data) ? response.data : response.data?.data || []
        }))
};

// Estilos
const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    marginTop: theme.spacing(4),
    borderRadius: '12px',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    padding: theme.spacing(1.5),
    borderRadius: '8px',
    fontWeight: 'bold',
    textTransform: 'none',
    fontSize: '16px',
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
    minWidth: '100%',
    marginBottom: theme.spacing(3),
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
    },
}));

const ValoresKwForm = () => {
    const [departamentoId, setDepartamentoId] = useState('');
    const [empresaId, setEmpresaId] = useState('');
    const [estratoId, setEstratoId] = useState('');
    const [valor, setValor] = useState('');
    const [fechaEdicion, setFechaEdicion] = useState(new Date());
    const [departamentos, setDepartamentos] = useState([]);
    const [empresas, setEmpresas] = useState([]);
    const [estratos, setEstratos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingDepartamentos, setLoadingDepartamentos] = useState(true);
    const [loadingEmpresas, setLoadingEmpresas] = useState(false);
    const [loadingEstratos, setLoadingEstratos] = useState(true);
    const { enqueueSnackbar } = useSnackbar();

    // Cargar departamentos y estratos al iniciar
    useEffect(() => {
        const cargarDatosIniciales = async () => {
            try {
                const [depResponse, estResponse] = await Promise.all([
                    DepartamentoService.obtenerTodos(),
                    EstratoService.obtenerTodos()
                ]);

                setDepartamentos(depResponse.data);
                setEstratos(estResponse.data);
            } catch (error) {
                enqueueSnackbar('Error al cargar datos iniciales', {
                    variant: 'error',
                    autoHideDuration: 5000
                });
                console.error('Error cargando datos:', error);
            } finally {
                setLoadingDepartamentos(false);
                setLoadingEstratos(false);
            }
        };

        cargarDatosIniciales();
    }, [enqueueSnackbar]);

    // Cargar empresas cuando cambia el departamento
    useEffect(() => {
        if (!departamentoId) {
            setEmpresas([]);
            setEmpresaId('');
            return;
        }

        const cargarEmpresas = async () => {
            setLoadingEmpresas(true);
            try {
                const response = await EmpresaService.obtenerPorDepartamento(departamentoId);
                setEmpresas(response.data);
            } catch (error) {
                enqueueSnackbar('Error al cargar empresas', {
                    variant: 'error',
                    autoHideDuration: 5000
                });
                console.error('Error cargando empresas:', error);
            } finally {
                setLoadingEmpresas(false);
            }
        };

        cargarEmpresas();
    }, [departamentoId, enqueueSnackbar]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!departamentoId) {
            enqueueSnackbar('Debe seleccionar un departamento', {
                variant: 'error',
                autoHideDuration: 3000
            });
            return;
        }

        if (!empresaId) {
            enqueueSnackbar('Debe seleccionar una empresa', {
                variant: 'error',
                autoHideDuration: 3000
            });
            return;
        }

        if (!estratoId) {
            enqueueSnackbar('Debe seleccionar un estrato', {
                variant: 'error',
                autoHideDuration: 3000
            });
            return;
        }

        if (!valor || isNaN(valor) || parseFloat(valor) <= 0) {
            enqueueSnackbar('El valor KW debe ser un número positivo', {
                variant: 'error',
                autoHideDuration: 3000
            });
            return;
        }

        setLoading(true);

        try {
            const response = await api.post('/agregar/guardar_valoreskw.php', {
                Id_empresaprestadoradeservicio: empresaId,
                Id_departamento: departamentoId,
                Id_estrato: estratoId,
                Valor: parseFloat(valor),
                fechaedicion: fechaEdicion.toISOString().split('T')[0] // Formato YYYY-MM-DD
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            if (response.data.success) {
                enqueueSnackbar(`Valor KW guardado con éxito`, {
                    variant: 'success',
                    autoHideDuration: 5000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right'
                    }
                });
                // Resetear formulario
                setDepartamentoId('');
                setEmpresaId('');
                setEstratoId('');
                setValor('');
                setFechaEdicion(new Date());
            } else {
                throw new Error(response.data.message || 'Error al guardar valor KW');
            }
        } catch (error) {
            console.error('Error:', error);
            let errorMessage = 'Ocurrió un problema al guardar el valor KW';

            if (error.response) {
                errorMessage = error.response.data.message || errorMessage;
            } else if (error.request) {
                errorMessage = 'No se pudo conectar con el servidor';
            }

            enqueueSnackbar(errorMessage, {
                variant: 'error',
                autoHideDuration: 5000,
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right'
                }
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Container maxWidth="md">
                <StyledPaper elevation={3}>
                    <Typography variant="h4" component="h1" gutterBottom sx={{
                        fontWeight: 'bold',
                        color: 'primary.main',
                        marginBottom: 3
                    }}>
                        Agregar Valor KW por Prestador
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit}>
                        <StyledFormControl variant="outlined" disabled={loadingDepartamentos || loading}>
                            <InputLabel id="departamento-label">Departamento</InputLabel>
                            <Select
                                labelId="departamento-label"
                                value={departamentoId}
                                onChange={(e) => {
                                    setDepartamentoId(e.target.value);
                                    setEmpresaId(''); // Resetear empresa al cambiar departamento
                                }}
                                label="Departamento"
                                required
                            >
                                <MenuItem value="">
                                    <em>Seleccione un departamento</em>
                                </MenuItem>
                                {departamentos.map((departamento) => (
                                    <MenuItem key={departamento.Id} value={departamento.Id}>
                                        {departamento.Nombre}
                                    </MenuItem>
                                ))}
                            </Select>
                        </StyledFormControl>

                        <StyledFormControl variant="outlined" disabled={!departamentoId || loadingEmpresas || loading}>
                            <InputLabel id="empresa-label">Empresa Prestadora</InputLabel>
                            <Select
                                labelId="empresa-label"
                                value={empresaId}
                                onChange={(e) => setEmpresaId(e.target.value)}
                                label="Empresa Prestadora"
                                required
                            >
                                <MenuItem value="">
                                    <em>{empresas.length ? 'Seleccione una empresa' : 'Primero seleccione un departamento'}</em>
                                </MenuItem>
                                {empresas.map((empresa) => (
                                    <MenuItem key={empresa.Id} value={empresa.Id}>
                                        {empresa.Nombre}
                                    </MenuItem>
                                ))}
                            </Select>
                        </StyledFormControl>

                        <StyledFormControl variant="outlined" disabled={loadingEstratos || loading}>
                            <InputLabel id="estrato-label">Estrato</InputLabel>
                            <Select
                                labelId="estrato-label"
                                value={estratoId}
                                onChange={(e) => setEstratoId(e.target.value)}
                                label="Estrato"
                                required
                            >
                                <MenuItem value="">
                                    <em>Seleccione un estrato</em>
                                </MenuItem>
                                {estratos.map((estrato) => (
                                    <MenuItem key={estrato.Id} value={estrato.Id}>
                                        {estrato.Nombre}
                                    </MenuItem>
                                ))}
                            </Select>
                        </StyledFormControl>

                        <StyledTextField
                            fullWidth
                            label="Valor KW"
                            variant="outlined"
                            type="number"
                            inputProps={{
                                step: "0.01",
                                min: "0.01"
                            }}
                            value={valor}
                            onChange={(e) => setValor(e.target.value)}
                            disabled={loading}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            helperText="Ingrese el valor por KW en COP"
                        />

                        <DatePicker
                            label="Fecha de Edición"
                            value={fechaEdicion}
                            onChange={(newValue) => setFechaEdicion(newValue)}
                            renderInput={(params) => (
                                <StyledTextField
                                    {...params}
                                    fullWidth
                                    margin="normal"
                                />
                            )}
                            disabled={loading}
                        />

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                            <StyledButton
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={loading || !departamentoId || !empresaId || !estratoId || !valor}
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                            >
                                {loading ? 'Guardando...' : 'Guardar Valor KW'}
                            </StyledButton>
                        </Box>
                    </Box>
                </StyledPaper>
            </Container>
        </LocalizationProvider>
    );
};

export default ValoresKwForm;